

import { useLocale, useTranslations } from "@/lib/i18n/intl";
import type { SubscriptionPlanView } from "@/lib/api/types";
import { SUBSCRIPTION_PLAN_TYPE } from "@/lib/api/types";
import { keys } from "@/lib/i18n/portal";
import { SubscriptionPlanEditModal } from "./SubscriptionPlanEditModal";

type Props = {
  plan: SubscriptionPlanView;
  canEdit?: boolean;
};

export function SubscriptionPlanCard({ plan, canEdit }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const formatAmount = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency || "EGP",
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${amount} ${currency ?? ""}`.trim();
    }
  };

  return (
    <div className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-sm shadow-[var(--shadow-sm)]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-[color:var(--text-primary)]">
            {plan.nameAr}
          </div>
          <div className="text-xs text-[color:var(--text-muted)]">
            {plan.nameEn || plan.nameAr}
          </div>
        </div>
        {canEdit ? <SubscriptionPlanEditModal plan={plan} /> : null}
      </div>

      <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-[color:var(--border-light)] px-3 py-2">
          <div className="text-[13px] text-[color:var(--text-muted)]">
            {t(keys.admin.subscriptionPlanAmount)}
          </div>
          <div className="text-sm font-semibold text-[color:var(--text-primary)]">
            {formatAmount(plan.amount, plan.currency)}
          </div>
        </div>
        <div className="rounded-lg border border-[color:var(--border-light)] px-3 py-2">
          <div className="text-[13px] text-[color:var(--text-muted)]">
            {t(keys.admin.subscriptionPlanPeriodDays)}
          </div>
          <div className="text-sm font-semibold text-[color:var(--text-primary)]">
            {plan.periodDays}
          </div>
        </div>
        <div className="rounded-lg border border-[color:var(--border-light)] px-3 py-2">
          <div className="text-[13px] text-[color:var(--text-muted)]">
            {t(keys.admin.subscriptionPlanType)}
          </div>
          <div className="mt-1">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[13px] font-semibold ${
                plan.type === SUBSCRIPTION_PLAN_TYPE.FREE_TRIAL
                  ? "bg-[color:var(--success-100)] text-[color:var(--success-700)]"
                  : "bg-[color:var(--primary-500)] text-white"
              }`}
            >
              {plan.type === SUBSCRIPTION_PLAN_TYPE.FREE_TRIAL
                ? t(keys.admin.subscriptionPlanTypeFreeTrial)
                : t(keys.admin.subscriptionPlanTypePaid)}
            </span>
          </div>
        </div>
        <div className="rounded-lg border border-[color:var(--border-light)] px-3 py-2">
          <div className="text-[13px] text-[color:var(--text-muted)]">
            {t(keys.common.status)}
          </div>
          <div className="mt-1">
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-[13px] font-semibold ${
                plan.isActive
                  ? "bg-[color:var(--success-100)] text-[color:var(--success-600)]"
                  : "bg-[color:var(--error-100)] text-[color:var(--error-600)]"
              }`}
            >
              {plan.isActive ? t(keys.common.active) : t(keys.common.inactive)}
            </span>
          </div>
        </div>
      </div>

    </div>
  );
}
